import React, { useState, useEffect, useRef } from 'react';
import { MdAdd } from 'react-icons/md';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import AntdModalComp from '../ReusableComponents/AntdModalComp';
import TmFilter from '../ReusableComponents/TmFilter';
import { DeletePopup } from '../ReusableComponents/DeletePopup';
import SurveyCategory from '../../Api/SurveyCategoryApi';
import AddSurveyCategory from './AddSurveyCategory';
import { SurveyCategoryHeader } from '../../constant/tableHeader';
import { toastError, toastSuccess, toastWarn } from '../Toast/ToastComponent';
import { getSurveyCategory } from '../../store/reducers/actionPermission';
import { ACTIVE_STATUS } from '../../constant/constant';
import SearchInput from '../ReusableComponents/SearchInput';

const defaultModalData = { title: '', id: null, status: true };

const filterArray = [
  {
    options: ACTIVE_STATUS,
    optionFilterProp: 'label',
    placeholder: 'Status',
    order: 1,
    col: 2,
    defaultValue: true,
    controlName: 'status',
    allowClear: true,
    input: 'select',
  },
  {
    order: 1,
    col: 'auto',
    className: 'ms-auto pe-0',
    input: 'button',
    type: 'dashed',
    htmlType: 'button',
    text: 'Reset',
  },
];

const SurveyCategoryComp = () => {
  const navigate = useNavigate();
  const debounceTimeoutRef = useRef(null);

  const [modal, setModal] = useState(false);
  const [surveyCategory, setSurveyCategoryState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalHeader, setModalHeader] = useState(null);
  const [modalData, setModalData] = useState(defaultModalData);

  const [filterArr, setFilterArr] = useState({
    search: '',
    status: true,
    page: 1,
    limit: 100000,
  });
  const hasFetched = useRef(false);
  const permission = useSelector(getSurveyCategory);

  const [buttonPermission, setButtonPermissions] = useState({
    add: undefined,
    edit: undefined,
    delete: undefined,
  });

  useEffect(() => {
    if (hasFetched.current) return;

    hasFetched.current = true;
    getSurveyCategoryList();
    if (permission) {
      handleButtonPermission();
    }
  }, [permission]);

  const handleButtonPermission = () => {
    if (!permission?.checked) {
      navigate('/');
    }

    let newObj = {};
    for (const element of permission?.actions) {
      const keyName = element?.statename?.toLowerCase();
      newObj[keyName] = element?.checked;
    }

    setButtonPermissions((prev) => ({
      ...prev,
      ...newObj,
    }));
  };

  const handleFilterChange = (obj) => {
    const newFilter = { ...filterArr, ...obj, page: 1 };
    setFilterArr(newFilter);
    getSurveyCategoryList(newFilter);
  };

  const getSurveyCategoryList = async (filterObj = filterArr) => {
    setLoading(true);
    try {
      const response = await SurveyCategory.getList(filterObj);
      if (response?.data?.success) {
        const list = response.data.result || [];
        const mappedData = list.map((item, index) => ({
          id: item.id,
          categoryName: item.categoryName,
          status: Boolean(item.activeStatus),
          deleteStatus: item.deleteStatus,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          serialNo: item.serialNo,
        }));
        setSurveyCategoryState(mappedData);
      } else {
        setSurveyCategoryState([]);
        toastError(response?.data?.message);
      }
    } catch (error) {
      setSurveyCategoryState([]);
      toastError(
        error?.response?.data?.message ||
          error?.message ||
          'Something went wrong',
      );
    }
    setLoading(false);
  };

  const toggleModal = (type, value) => {
    if (type === 'Close') {
      setModal(false);
      setModalHeader(null);
      setModalData(defaultModalData);
      return;
    }

    if (value && (type === 'Edit' || type === 'View')) {
      setModalData({
        title: value.categoryName,
        id: value.id,
        status: Boolean(value.status),
      });
    } else {
      setModalData(defaultModalData);
    }

    setModalHeader(<span>{`${type} Survey Category`}</span>);
    setModal(true);
  };

  const callDelete = (id) => {
    DeletePopup(async (canDelete) => {
      if (canDelete) {
        try {
          const response = await SurveyCategory.delete(id);
          if (response.data.success) {
            toastSuccess(response.data.message);
            getSurveyCategoryList(filterArr);
          } else {
            toastWarn(response.data.message);
          }
        } catch (error) {
          toastError(
            error?.response?.data?.message ||
              error?.message ||
              'Something went wrong',
          );
        }
      }
    });
  };

  const handleSearchChange = (value) => {
    const newFilter = { ...filterArr, search: value, page: 1 };
    setFilterArr(newFilter);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      getSurveyCategoryList(newFilter);
    }, 500);
  };

  const handleReset = () => {
    const resetFilter = {
      search: '',
      status: true,
      page: 1,
      limit: 10,
    };
    setFilterArr(resetFilter);
    getSurveyCategoryList(resetFilter);
  };

  const updatedFilterArray = filterArray.map((item) =>
    item.input === 'button' && item.text === 'Reset'
      ? { ...item, onClick: handleReset }
      : item,
  );

  return (
    <>
      <div className="d-md-flex">
        <h5>Survey Category</h5>
        <div className="ms-md-auto d-flex">
          <div className="me-2">
            <SearchInput
              placeholder="Search"
              value={filterArr.search}
              onSearchChange={handleSearchChange}
            />
          </div>
          {buttonPermission?.add && (
            <Button
              type="primary"
              onClick={() => toggleModal('Add')}
              icon={<MdAdd size={20} />}
            />
          )}
        </div>
      </div>

      <div className="mt-2">
        <TmFilter
          filterArray={updatedFilterArray}
          handleFilterChange={handleFilterChange}
          initialValues={{ status: true }}
        />
      </div>

      <div className="mt-3">
        <TmMasterTable
          buttonPermission={buttonPermission}
          rows={surveyCategory}
          columns={SurveyCategoryHeader}
          custom={['checkbox', 'action']}
          toggleModal={toggleModal}
          handleDelete={callDelete}
          loading={loading}
          noDataText="No Data Found"
        />
      </div>

      <AntdModalComp
        width="30%"
        toggleModal={() => toggleModal('Close')}
        modalOpen={modal}
        modalHeader={modalHeader}
        modalBody={
          <AddSurveyCategory
            modalHeader={modalHeader}
            getSurveyCategoryList={getSurveyCategoryList}
            setModal={setModal}
            modal={modal}
            modalData={modalData}
            filterArr={filterArr}
          />
        }
      />
    </>
  );
};

export default SurveyCategoryComp;
